import ApiService from '../../../../services/apiService';
import { mapState } from 'vuex';
import momentTZ from 'moment-timezone';
import { isArray, cloneDeep, pullAt, set, get, isEmpty } from 'lodash';
export default {
  name: 'CoreDtwinRuleEdit',
  props: {
    value: {
      type: Object,
      default: () => ({}),
      required: false
    },
    // the states that are to be displayed / edited
    states: {
      type: Array,
      required: true
    },
    dtwin: {
      type: Object,
      required: false,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    hasTwinEditPermission: {
      type: Boolean,
      required: false,
      default: false
    },
    isOpen: {
      type: Boolean,
      default: true
    },
    template: {
      type: Object,
      required: false,
      default: () => ({})
    },
    isCreatedByTemplate: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      formValid: false,
      editedRule: {},
      ruleModeMetricsDisablePrompt: false,
      ruleModes: {
        metrics: true,
        geofences: false
      },
      modeTab: 'metrics',
      // if states are refreshed on a websocket-update, this would also destroy the whole form
      // so make a copy here initially and only use that
      statesCopy: [],
      testStateMetrics: {},
      testResults: null
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    requiredRule() {
      return [v => !!v || parseInt(v) === 0 || this.$i18n_t('Common.fieldRequired')];
    },
    hourRules() {
      return [...this.requiredRule, v => v >= 0 && v < 24 || this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.timeTriggers.hourRule')];
    },
    throttleRules() {
      return [...this.requiredRule, v => v >= 0 || this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.geoFences.numberRule')];
    },
    executionTimes() {
      return (get(this.editedRule, 'properties.executionTimes') || []).filter(t => {
        return !!get(t, 'time') && t.time.constructor === Object;
      });
    },
    timeTriggers() {
      return (get(this.editedRule, 'properties.timeTriggers') || []).filter(t => {
        return !!get(t, 'time') && t.time.constructor === Object;
      });
    },
    aggregateStates() {
      return this.statesCopy.filter(state => {
        return ['virtual-device', 'virtual-device-aggregate', 'aggregate', 'timeseries-aggregate'].indexOf(state.properties.binding.type) >= 0;
      });
    },
    isTemplateContext() {
      return !isEmpty(this.template);
    }
  },
  watch: {
    value() {
      if (!get(this.editedRule, 'properties') || get(this.value, 'id') !== get(this.editedRule, 'id')) {
        if (get(this.value, 'properties')) {
          this.editedRule = cloneDeep(this.value);
        } else {
          this.createDefaultRule();
        }
      }
      this.addTimeTriggerDefault();
      this.addGeoFenceDefault();
      this.addTriggeredAggregatesDefault();
      this.ruleModes.metrics = this.editedRule.properties.stateMetrics.length > 0;
      this.ruleModes.geofences = this.editedRule.properties.geoFences.length > 0;
    },
    states() {
      if (this.statesCopy.length === 0) {
        this.statesCopy = cloneDeep(this.states);
      }
    }
  },
  created() {
    this.createDefaultRule();
  },
  mounted() {
    if (get(this.value, 'properties')) {
      this.editedRule = cloneDeep(this.value);
    }
    this.addExecutionTimeDefault();
    this.addTimeTriggerDefault();
    this.addGeoFenceDefault();
    this.addTriggeredAggregatesDefault();
    if (get(this.value, 'properties')) {
      this.ruleModes.metrics = this.editedRule.properties.stateMetrics.length > 0;
      this.ruleModes.geofences = this.editedRule.properties.geoFences.length > 0;
    }
    this.statesCopy = cloneDeep(this.states);
    if (this.editedRule.id) {
      // validate form for editing rules
      // to highlight incorrectly linked states immediately
      this.$refs.form.validate();
    }
  },
  methods: {
    addExecutionTimeDefault() {
      if (!get(this.editedRule, 'properties.executionTimes') || !isArray(get(this.editedRule, 'properties.executionTimes'))) {
        this.editedRule.properties.executionTimes = [];
      }
      this.editedRule.properties.executionTimes = (get(this.editedRule, 'properties.executionTimes') || []).filter(t => {
        return !!get(t, 'startTime') && t.startTime.constructor === Object;
      });
    },
    addTimeTriggerDefault() {
      // if (
      //   !get(this.editedRule, 'properties.timeTriggers[0].time') ||
      //   get(this.editedRule, 'properties.timeTriggers[0].time').constructor !== Object
      // ) {
      //   this.editedRule.properties.timeTriggers = [{ timeZone: momentTZ.tz.guess(), time: { hourly: false } }];
      // }
      if (!get(this.editedRule, 'properties.timeTriggers') || !isArray(get(this.editedRule, 'properties.timeTriggers'))) {
        this.editedRule.properties.timeTriggers = [];
      }
      this.editedRule.properties.timeTriggers = (get(this.editedRule, 'properties.timeTriggers') || []).filter(t => {
        return !!get(t, 'time') && t.time.constructor === Object;
      });
    },
    addGeoFenceDefault() {
      if (!get(this.editedRule, 'properties.geoFences') || !isArray(get(this.editedRule, 'properties.geoFences'))) {
        this.editedRule.properties.geoFences = [];
        if (this.editedRule.properties.conditionEnabled === undefined) {
          this.editedRule.properties.conditionEnabled = true;
        }
      }
    },
    addTriggeredAggregatesDefault() {
      if (!get(this.editedRule, 'properties.triggeredAggregates') || !isArray(get(this.editedRule, 'properties.triggeredAggregates'))) {
        this.editedRule.properties.triggeredAggregates = [];
      }
    },
    createDefaultRule() {
      if (get(this.value, 'properties')) {
        this.editedRule = cloneDeep(this.value);
      } else {
        this.editedRule = {
          stateIds: [],
          properties: {
            executionTimes: [],
            timeTriggers: [],
            geoFences: [],
            stateMetrics: [],
            conditions: {
              type: 'and',
              items: []
            },
            actions: [],
            triggeredAggregates: [],
            conditionEnabled: true
          }
        };
        this.editRuleAddState(0);
      }
    },
    stateMetricModeChanged() {
      if (!this.ruleModes.metrics) {
        this.ruleModeMetricsDisablePrompt = true;
      } else {
        this.$nextTick(() => {
          this.toggleStateMetricMode();
        });
      }
    },
    toggleStateMetricMode() {
      this.editedRule.properties.conditionEnabled = true;
      if (!this.ruleModes.metrics) {
        this.editedRule.properties.stateMetrics = [];
      } else {
        if (this.editedRule.properties.stateMetrics.length === 0) {
          this.editRuleAddState(0);
        }
      }
    },
    toggleGeofenceMode() {
      if (!this.ruleModes.geofences) {
        this.editedRule.properties.geoFences = [];
      }
    },
    toggleRuleState() {
      this.editedRule.properties.disabled = !get(this.editedRule, 'properties.disabled', false);
      this.editedRule = cloneDeep(this.editedRule);
      this.saveRule();
    },
    editRuleAddState(index) {
      this.editedRule.properties.stateMetrics.splice(index, 0, {
        history: {
          count: 1,
          useCount: true,
          minutes: 60,
          useMinutes: true,
          abortIfNull: true
        },
        transform: 'x',
        operation: 'last'
      });
      this.editedRule = cloneDeep(this.editedRule);
    },
    editRuleDeleteState(index) {
      const stateMetricKey = this.editedRule.properties.stateMetrics[index].key;
      const filterConditions = conditions => {
        conditions.items = conditions.items.filter(cond => {
          if (!!cond.compOperator) {
            return cond.key !== stateMetricKey;
          } else {
            filterConditions(cond);
            return true;
          }
        });
      };
      filterConditions(this.editedRule.properties.conditions);
      pullAt(this.editedRule.properties.stateMetrics, [index]);
      if (this.editedRule.properties.stateMetrics.length === 0) {
        this.editRuleAddState(0);
      }
      this.editRuleGenerateCleanStateIds();
      this.editedRule = cloneDeep(this.editedRule);
    },
    editRuleAddGeoFence(index) {
      this.editedRule.properties.geoFences.splice(index, 0, {});
      this.editedRule = cloneDeep(this.editedRule);
      // highlight the twin-finder field
      this.$refs['form'].validate();
    },
    editRuleDeleteGeoFence(index) {
      pullAt(this.editedRule.properties.geoFences, [index]);
      this.editedRule = cloneDeep(this.editedRule);
    },
    editRuleAddExecutionTime(index) {
      this.editedRule.properties.executionTimes.splice(index, 0, {
        timeZone: momentTZ.tz.guess(),
        startTime: {},
        endTime: {},
        weekDays: [this.$i18n_t('Common.AllDays')],
        monthDays: [this.$i18n_t('Common.AllDays')]
      });
      this.editedRule = cloneDeep(this.editedRule);
    },
    editRuleDeleteExecutionTime(index) {
      pullAt(this.editedRule.properties.executionTimes, [index]);
      this.editedRule = cloneDeep(this.editedRule);
    },
    editRuleAddTimeTrigger(index) {
      this.editedRule.properties.timeTriggers.splice(index, 0, {
        timeZone: momentTZ.tz.guess(),
        time: {
          hourly: false
        }
      });
      this.editedRule = cloneDeep(this.editedRule);
    },
    editRuleDeleteTimeTrigger(index) {
      pullAt(this.editedRule.properties.timeTriggers, [index]);
      if (this.editedRule.properties.timeTriggers.length === 0) {
        // if the last time-trigger is removed, also auto-disable (if it even was enabled) the "time-trigger-only" option
        // will show the other options like time-trigger conditons and JS-conditions and such again in the UI
        this.editedRule.properties.timeTriggerOnly = false;
      }
      this.editedRule = cloneDeep(this.editedRule);
    },
    editRuleDeleteAction(index) {
      pullAt(this.editedRule.properties.actions, [index]);
      this.editedRule.properties.actions = cloneDeep(this.editedRule.properties.actions);
    },
    handleCommandChanged(index) {
      const selectedCommand = this.editedRule.properties.actions[index];
      const selectedCommandName = selectedCommand.command;
      const initalCommand = get(this.value, `properties.actions[${index}]`) || {};
      const initalCommandName = initalCommand.command;
      selectedCommand.params = selectedCommandName === initalCommandName ? initalCommand.params : {};
    },
    editRuleAddAggregate(index) {
      this.editedRule.properties.triggeredAggregates.splice(index, 0, {});
      this.editedRule.properties = cloneDeep(this.editedRule.properties);
    },
    editRuleDeleteAggregate(index) {
      pullAt(this.editedRule.properties.triggeredAggregates, [index]);
      this.editedRule.properties.triggeredAggregates = cloneDeep(this.editedRule.properties.triggeredAggregates);
    },
    editRuleGenerateCleanStateIds() {
      let stateIds = [];
      this.editedRule.properties.stateMetrics.forEach(sm => {
        if (sm.stateId) {
          if (stateIds.findIndex(sid => sid === sm.stateId) < 0) {
            stateIds.push(sm.stateId);
          }
        } else if (sm.id) {
          if (stateIds.findIndex(sid => sid === sm.id) < 0) {
            stateIds.push(sm.id);
          }
        }
      });
      this.editedRule.stateIds = stateIds;
    },
    testRule() {
      let finalStateMetricValues = {};
      // make sure JSON objects are handled correctly
      // strings, bools, numbers should be unaffected still
      Object.keys(this.testStateMetrics).forEach(metricKey => {
        try {
          finalStateMetricValues[metricKey] = JSON.parse(this.testStateMetrics[metricKey]);
        } catch (_) {
          // if the JSON cannot be parsed, interpret it as string directly
          finalStateMetricValues[metricKey] = this.testStateMetrics[metricKey];
        }
      });
      return ApiService.digitalTwinRuleTest(this, {
        rule: this.editedRule,
        stateValues: finalStateMetricValues
      }).then(result => {
        Object.keys(result.ruleMetrics).forEach(metricKey => {
          if (result.ruleMetrics[metricKey] === null) {
            // make sure null values are displayed in the UI
            result.ruleMetrics[metricKey] = 'null';
          }
        });
        this.testResults = result;
      });
    },
    didSelectState(stateMetric) {
      if (!stateMetric.key || stateMetric.key.trim() === '') {
        const matchIdentifier = this.isTemplateContext ? 'key' : 'id';
        const identifier = this.isTemplateContext ? 'id' : 'stateId';
        const foundState = this.statesCopy.find(s => {
          const identifierValue = this.isTemplateContext ? stateMetric[identifier].toLowerCase() // string from template
          : stateMetric[identifier]; // actual state number
          return s[matchIdentifier] === identifierValue;
        });
        if (foundState) {
          stateMetric.key = foundState.key.replace(/[^A-Za-z0-9]/is, '');
        } else {
          stateMetric.key = stateMetric.title || stateMetric[identifier] || 'unknown';
        }
      }
      if (!this.$refs.metrics.stateIsNumeric(stateMetric)) {
        // for all non-numeric values there is no offering of any operation but "LAST", so it makes no sense to allow a history
        stateMetric.history.count = 1;
        stateMetric.history.useCount = true;
      }
      this.editRuleGenerateCleanStateIds();
      if (!get(this.editedRule, 'properties.conditions.items')) {
        set(this.editedRule, 'properties.conditions.items', []);
      }
      if (get(this.editedRule, 'properties.conditions.items.length') === 0) {
        this.editedRule.properties.conditions.items.push({
          key: stateMetric.key,
          compOperator: 'gt',
          compValue: 0
        });
      }
      this.validateForm();
    },
    refreshForm(newRule = null) {
      this.editedRule = cloneDeep(newRule || this.editedRule);
    },
    validateForm() {
      this.$refs.form.validate();
    },
    saveRule() {
      this.$emit('save', this.editedRule);
    }
  }
};